import useSmoothScroll from '@/hooks/useSmoothScroll';
import mobilePhones from '@/static/assets/homeowners/mobile_phones.svg';
import mobilesLeftQuote from '@/static/assets/homeowners/mobilesLeftQuote.svg';
import mobilesRightQuote from '@/static/assets/homeowners/mobilesRightQuote.svg';
import phoneOneConfig from '@/static/assets/homeowners/phone_left.json';
import phoneTwoConfig from '@/static/assets/homeowners/phone_right.json';
import { Box, Flex, useLottie, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { clampedInversedLerp, lerp } from '@lemonade-hq/ts-helpers';
import type React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

const AppSectionContainer = styled(Box)`
  position: relative;
`;

const Phone = styled(Box)`
  width: 180px;
  height: 344px;

  &:after {
    display: none;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        height: 480px;
        width: 250px;

        &:first-child {
            &:after {
                content: '';
                display: block;
                width: 125px;
                height: 105px;
                position: absolute;
                top: 15%;
                right: calc(100% + 30px);
                background-image: url(${mobilesLeftQuote.src});
                background-size: contain;
                background-repeat: no-repeat;
                will-change: right; /* the calc causes a re-paint, this helps */
            }
        }

        &:last-child {
            &:after {
                content: '';
                display: block;
                width: 108px;
                height: 102px;
                position: absolute;
                top: 0;
                left: calc(100% + 30px);
                background-image: url(${mobilesRightQuote.src});
                background-size: contain;
                background-repeat: no-repeat;
            }
        }
    `}
`;

const Phones = styled(Flex)`
  display: none;

  ${MEDIA_QUERIES.desktop`
        display: flex;
    `}
`;

const PhonesMobile = styled(Box)`
  background-image: url(${mobilePhones.src});
  background-size: contain;
  background-repeat: no-repeat;
  display: block;

  ${MEDIA_QUERIES.desktop`
        display: none;
    `}

  @media (max-width: 375px) {
    width: 300px;
  }
`;

interface Props {
  mobileFrom: number;
  mobileTo: number;
  desktopFrom: number;
  desktopTo: number;
}

const AppsSection: React.FC<Props> = ({ mobileFrom, mobileTo, desktopFrom, desktopTo }) => {
  const { isMobileViewport } = useViewportSize();
  const { smoothRelativeScroll: t } = useSmoothScroll();
  const phonesRef = useRef<Element>(null);
  const animationContainerOneRef = useRef<Element>(null);
  const animationContainerTwoRef = useRef<Element>(null);
  const sectionRef = useRef<Element>(null);

  const played = useRef(false);

  const slideLerp = useMemo(
    () => clampedInversedLerp(isMobileViewport ? mobileFrom : desktopFrom, isMobileViewport ? mobileTo : desktopTo, t),
    [t, desktopFrom, desktopTo, mobileFrom, mobileTo, isMobileViewport],
  );

  const lottieReferenceOne = useLottie(animationContainerOneRef, {
    animationData: phoneOneConfig,
    lottieConfig: {
      loop: true,
    },
  });

  const lottieReferenceTwo = useLottie(animationContainerTwoRef, {
    animationData: phoneTwoConfig,
    lottieConfig: {
      loop: true,
    },
  });

  useEffect(() => {
    if (
      lottieReferenceOne?.play &&
      lottieReferenceTwo &&
      lottieReferenceTwo.play &&
      slideLerp > desktopTo &&
      !played.current
    ) {
      played.current = true;
      lottieReferenceOne.play();
      lottieReferenceTwo.play();
    }
  }, [lottieReferenceOne, lottieReferenceTwo, slideLerp, desktopTo]);

  return (
    <AppSectionContainer ref={sectionRef}>
      <Flex alignItems="center" justifyContent="center" flexDirection="row" height="556px" mt={[25, 25, 0]}>
        <PhonesMobile width={350} height={478} backgroundImage={mobilePhones.src} />

        <Phones
          alignItems="center"
          justifyContent="space-between"
          ref={phonesRef}
          position="relative"
          height={[383, 383, 555]}
          width={[330, 330, 538]}
        >
          <Phone
            aria-hidden={true}
            style={{
              alignSelf: 'flex-start',
              transform: `translateY(${lerp(0, 40, slideLerp)}px)`,
            }}
            factor={slideLerp}
          >
            <Box ref={animationContainerOneRef} height="100%" />
          </Phone>
          <Phone
            aria-hidden={true}
            style={{
              alignSelf: 'flex-end',
              transform: `translateY(${lerp(0, -40, slideLerp)}px)`,
            }}
            factor={slideLerp}
          >
            <Box ref={animationContainerTwoRef} height="100%" />
          </Phone>
        </Phones>
      </Flex>
    </AppSectionContainer>
  );
};

export default AppsSection;
